import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function AuthHome() {
  const navigate = useNavigate();

  // ✅ Permet de vérifier VISUELLEMENT que le build déployé contient bien ce fichier
  const BUILD_ID = "AuthHome-resend-debug-2026-01-02";

  const [emailResend, setEmailResend] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendOk, setResendOk] = useState(null);
  const [resendErr, setResendErr] = useState(null);

  /**
   * ✅ Liste d’endpoints possibles.
   * IMPORTANT :
   * - Dans ton backend, la route exacte peut n’exister que sous UNE forme.
   * - Ici on n’avale plus silencieusement : on remonte le status + endpoint testé.
   */
  const RESEND_ENDPOINTS = useMemo(
    () => [
      "/api/v2/auth/resend-activation",
      "/api/v2/auth/resend_activation",
      "/api/v2/auth/resend",
      "/api/v2/auth/resend-registration",
      "/api/v2/auth/resend_registration",
      "/api/v2/auth/register/resend",
    ],
    []
  );

  async function postJsonWithTimeout(url, payload, timeoutMs = 8000) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      // certains backends peuvent renvoyer du texte/HTML en cas d’erreur,
      // donc on sécurise le parsing JSON.
      let json = {};
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        json = await res.json().catch(() => ({}));
      } else {
        const text = await res.text().catch(() => "");
        // on garde un extrait utile
        json = text ? { _raw: text.slice(0, 400) } : {};
      }

      return { res, json };
    } finally {
      clearTimeout(t);
    }
  }

  async function tryResendActivation(email) {
    const payload = { email: String(email || "").trim() };

    let lastNon404 = null;
    let lastError = null;

    for (const url of RESEND_ENDPOINTS) {
      try {
        console.log("[AUTH][RESEND] trying", url, payload);

        const { res, json } = await postJsonWithTimeout(url, payload, 8000);

        console.log("[AUTH][RESEND] result", {
          url,
          status: res.status,
          ok: res.ok,
          json,
        });

        // ✅ succès
        if (res.ok && json?.ok) return { ok: true, json, url, status: res.status };

        // 404 => on tente l’endpoint suivant, mais on garde une trace
        if (res.status === 404) {
          lastError = { url, status: 404, json };
          continue;
        }

        // autre status => on s’arrête : c’est probablement LA bonne route mais en erreur
        lastNon404 = { url, status: res.status, json };
        break;
      } catch (err) {
        // abort, réseau, CORS, etc.
        console.warn("[AUTH][RESEND] fetch error on", url, err);
        lastError = { url, status: null, json: { error: String(err?.message || err) } };
        // on tente le suivant
      }
    }

    if (lastNon404) {
      const msg =
        lastNon404.json?.error ||
        lastNon404.json?.message ||
        lastNon404.json?._raw ||
        `Erreur (${lastNon404.status})`;
      return {
        ok: false,
        url: lastNon404.url,
        status: lastNon404.status,
        error: `Échec sur ${lastNon404.url} : ${msg}`,
      };
    }

    if (lastError) {
      const msg =
        lastError.json?.error ||
        lastError.json?.message ||
        lastError.json?._raw ||
        (lastError.status ? `HTTP ${lastError.status}` : "Erreur réseau/timeout");
      return {
        ok: false,
        url: lastError.url,
        status: lastError.status,
        error: `Aucune route compatible trouvée. Dernier essai: ${lastError.url} → ${msg}`,
      };
    }

    return {
      ok: false,
      url: null,
      status: null,
      error: "Impossible de renvoyer le lien (aucune route backend compatible trouvée).",
    };
  }

  async function onResend(e) {
    e.preventDefault();
    setResendOk(null);
    setResendErr(null);

    const email = String(emailResend || "").trim();
    if (!email) {
      setResendErr("Veuillez saisir votre email.");
      return;
    }

    console.log("[AUTH][RESEND] submit", { email, BUILD_ID });
    setResendLoading(true);

    const out = await tryResendActivation(email);

    setResendLoading(false);

    if (out.ok) {
      setResendOk(
        (out.json?.message
          ? `${out.json.message} (via ${out.url})`
          : `Lien d’activation renvoyé. Vérifiez votre email (et les spams). (via ${out.url})`)
      );
    } else {
      setResendErr(out.error || "Erreur lors de l’envoi du lien.");
    }
  }

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f3f4f6",
      padding: "clamp(16px, 3dvh, 24px)",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      width: "min(calc(100vw - 72px), 1180px)",
      maxWidth: "calc(100vw - 48px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "20px",
      padding: "clamp(38px, 4vw, 56px)",
      boxShadow: "0 24px 64px rgba(15,23,42,0.13)",
    },
    h1: {
      margin: 0,
      fontSize: "clamp(42px, 3.1vw, 58px)",
      fontWeight: 950,
      color: "#111827",
      lineHeight: 1.08,
      letterSpacing: 0,
    },
    subtitle: {
      display: "inline-block",
      marginTop: "8px",
      fontSize: "clamp(26px, 2vw, 38px)",
      fontWeight: 750,
      lineHeight: 1.18,
      color: "#1f2937",
    },
    instructions: {
      margin: "clamp(28px, 2.8vw, 42px) auto 0",
      maxWidth: "900px",
      display: "grid",
      gap: "14px",
      color: "#374151",
      lineHeight: 1.55,
      fontSize: "clamp(18px, 1.25vw, 24px)",
    },
    instructionLine: {
      margin: 0,
      padding: "0 0 0 18px",
      borderLeft: "4px solid #d1d5db",
    },
    instructionLabel: {
      color: "#111827",
      fontWeight: 950,
    },
    p: {
      marginTop: "28px",
      color: "#4b5563",
      lineHeight: 1.65,
      fontSize: "24px",
    },
    row: {
      display: "flex",
      gap: "14px",
      flexWrap: "wrap",
      justifyContent: "center",
      marginTop: "clamp(28px, 3vw, 42px)",
    },
    btn: {
      padding: "15px 28px",
      borderRadius: "9999px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      fontSize: "16px",
      fontWeight: 900,
      cursor: "pointer",
    },
    btnLight: {
      padding: "15px 28px",
      borderRadius: "9999px",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      fontSize: "16px",
      fontWeight: 900,
      cursor: "pointer",
    },
    footer: {
      marginTop: "28px",
      fontSize: "14px",
      color: "#6b7280",
      textAlign: "center",
    },
    mail: { color: "#111827", fontWeight: 800, textDecoration: "none" },

    build: {
      marginTop: "8px",
      fontSize: "12px",
      color: "#9ca3af",
      userSelect: "text",
    },

    box: {
      marginTop: "18px",
      padding: "14px",
      borderRadius: "14px",
      border: "1px solid #e5e7eb",
      background: "#fafafa",
    },
    label: { fontSize: "13px", fontWeight: 900, color: "#111827" },
    input: {
      width: "100%",
      padding: "12px 12px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: "14px",
      marginTop: "8px",
      background: "#fff",
    },
    small: { marginTop: "8px", fontSize: "12px", color: "#6b7280" },
    alertOk: {
      marginTop: "12px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
      fontSize: "14px",
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    alertErr: {
      marginTop: "12px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <h1 style={{ ...S.h1, textAlign: "center" }}>
          Connexion<br />
          <span style={S.subtitle}>Anesthésie-Réanimation Cochin–Port-Royal</span>
        </h1>
        <div style={S.instructions}>
          <p style={S.instructionLine}>
            <span style={S.instructionLabel}>Nouveau ?</span> Créez votre compte
            depuis la liste des médecins avec votre adresse email.
          </p>
          <p style={S.instructionLine}>
            <span style={S.instructionLabel}>Déjà inscrit ?</span> Connectez-vous
            avec votre adresse mail et votre mot de passe.
          </p>
          <p style={S.instructionLine}>
            <span style={S.instructionLabel}>Pas dans la liste ?</span> Écrire à{" "}
            <a style={S.mail} href="mailto:christophe.baillard@aphp.fr">
              christophe.baillard@aphp.fr
            </a>
            .
          </p>
        </div>

        <div style={S.row}>
          <button
            type="button"
            style={S.btn}
            onClick={() => navigate("/auth/register")}
          >
            Créer mon compte
          </button>

          <button
            type="button"
            style={S.btnLight}
            onClick={() => { window.location.href = "/auth/login"; }}
            title="Si vous avez déjà un compte et un mot de passe"
          >
            Se connecter
          </button>
        </div>

        <div style={S.footer}>
          Note : la saisie des desiderata reste accessible comme avant via{" "}
          <b>/medecin</b>.
        </div>
      </div>
    </div>
  );
}
